import { useState } from "react"
import type { ReactNode } from "react"

import { useTranslation } from "react-i18next"

import { Button } from "@/components/ui/button"
import { Separator } from "@/components/ui/separator"
import {
  SidebarContent,
  SidebarFooter,
  SidebarMenu,
  SidebarMenuItem,
} from "@/components/ui/sidebar"
import { ContextSidebarSearch } from "@/features/chat/components/context-sidebar-search"
import { ProjectEmbeddingIndicator } from "@/features/projects/components/project-embedding-indicator"
import { ProjectSettingsDialog } from "@/features/projects/components/project-settings-dialog"
import { useProject } from "@/hooks/use-projects"
import { useResearchPreferencesStore } from "@/stores/research-preferences-store"

import { ResearchSettingsContent } from "./research-settings-content"

type ResearchSettingsSidebarProps = {
  sessionType: string
  projectId?: string
  renderContextContent?: (searchQuery: string) => ReactNode
}

export function ResearchSettingsSidebar({
  sessionType,
  projectId,
  renderContextContent,
}: ResearchSettingsSidebarProps) {
  const { t } = useTranslation()
  const { t: tFiles } = useTranslation("", { keyPrefix: "project_settings.files" })
  const [settingsOpen, setSettingsOpen] = useState(false)
  const [contextSearchQuery, setContextSearchQuery] = useState("")

  const { data: project } = useProject(projectId, sessionType)

  const researchPrefsStore = useResearchPreferencesStore(sessionType)
  const resetPreferences = researchPrefsStore((state) => state.resetPreferences)

  const handleResetAllSettings = () => {
    resetPreferences()
  }

  return (
    <>
      <SidebarContent className="flex flex-col gap-4 overflow-hidden p-2">
        {projectId && (
          <>
            <div className="flex flex-col gap-3">
              <span className="text-muted-foreground text-xs font-semibold tracking-wider uppercase">
                {tFiles("project_files")}
              </span>
              <ProjectEmbeddingIndicator
                projectId={projectId}
                onClick={() => setSettingsOpen(true)}
              />
            </div>
            <Separator />
          </>
        )}
        {renderContextContent && (
          <ContextSidebarSearch value={contextSearchQuery} onChange={setContextSearchQuery} />
        )}
        {renderContextContent?.(contextSearchQuery)}
        <span className="text-muted-foreground text-xs font-semibold tracking-wider uppercase">
          {t("pages.ai_explorer.scholar_settings")}
        </span>
        <SidebarMenu className="flex h-full min-h-0 flex-1">
          <SidebarMenuItem className="flex h-full min-h-0 flex-1 flex-col">
            <ResearchSettingsContent sessionType={sessionType} />
          </SidebarMenuItem>
        </SidebarMenu>

        <SidebarFooter className="mt-auto shrink-0 sm:justify-normal">
          <Button variant="destructive" onClick={handleResetAllSettings}>
            {t("pages.common.reset_settings")}
          </Button>
        </SidebarFooter>
      </SidebarContent>
      {projectId && (
        <ProjectSettingsDialog
          defaultSection="files"
          open={settingsOpen}
          project={project}
          projectId={projectId}
          sessionType={sessionType}
          onOpenChange={setSettingsOpen}
        />
      )}
    </>
  )
}
